<template>
  <div class="cashier-report">
    <SDialog v-model:open="modalVisible">
      <SDialogContent
        :show-close-button="false"
        class="cashier-report !fixed !inset-0 !top-0 !left-0 !w-screen !h-screen !max-w-none !rounded-none !translate-x-0 !translate-y-0 !p-0 !border-0 overflow-y-auto"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("button.cashier-report") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="customized-header d-flex flex-row fl-s-b">
          <div class="header-title d-flex fl-col fl-j-c">
            <div>
              <p>{{ $t("button.cashier-report") }}</p>
            </div>
          </div>
          <button class="close-button d-flex fl-col fl-j-c" @click="close">
            <SvgIcon
              :iconContent="closeIconContent"
              :iconAlt="closeIconAlt"
              :iconStyle="closeIconStyle"
            />
          </button>
        </div>

        <div v-if="isLoading" class="progressbar-wrapper d-flex fl-col fl-s-b">
          <div class="img-wrapper">
            <img src="@/assets/images/icons/girl-running.png" alt="Logo" />
          </div>
          <div>
            <p class="loading-text">{{ $t("common.loading") }}</p>
          </div>
          <div>
            <el-progress
              :percentage="70"
              :indeterminate="true"
              :stroke-width="26"
              :show-text="false"
            />
          </div>
          <div class="one-sec-text">
            <p>{{ $t("common.one-sec") }}</p>
          </div>
        </div>

        <iframe v-else-if="embedUrl" :src="embedUrl" class="omni-dashboard" />

        <div v-else class="explo-analytics-dashboard">
          <explo-dashboard
            :dash-customer-token="getCustomeToken()"
            :updateUrlParams="true"
            :isProduction="true"
            :refresh-minutes="1"
            :variables="
              JSON.stringify(
                Object.assign({}, { sellerId }, generateVariablesObject())
              )
            "
          >
          </explo-dashboard>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { useI18n } from "vue-i18n";
import { getGroupExploToken } from "@/api/gql/group";
import closeIcon from "@/assets/images/svg/close.svg";
import ModalModule from "@/store/modules/modal";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import {
  fetchOmniEmbedUrl,
  fetchPosDashboards,
  IPosDashboard,
} from "@/api/quicksight/quicksight";
import { getLanguage } from "@/utils/helpers/lang";
import { exploTranslations } from "@/constants/dashboards";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: ModalComponents.Analytics,
  components: {
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const closeIconStyle =
      "width: 30px; height: 30px; filter: contrast(170%) invert(100%);";
    const closeIconContent = closeIcon;
    const closeIconAlt = "Close";
    const modalVisible = dialogVisibleForStatus(ModalStatus.CashierReports);
    const { t } = useI18n({});
    const isLoading = ref(true);
    const dashboardId = "L9Ymn7qxXl";
    const dashboardToken = ref("");
    const embedUrl = ref("");
    const sellerId = UserModule.userId;

    function getCustomeToken() {
      return `${dashboardId}:${dashboardToken.value}`;
    }

    function generateVariablesObject() {
      if (Object.keys(exploTranslations).includes(dashboardId)) {
        return pullTranslations(exploTranslations[dashboardId]);
      }

      return {};
    }

    function pullTranslations(translations: any): any {
      const translationsEntries: any = Object.entries(translations).map(
        ([key, value]) => [key, String(t(value as string))]
      );
      return Object.fromEntries(translationsEntries);
    }

    return {
      modalVisible,
      closeIconStyle,
      closeIconContent,
      closeIconAlt,
      isLoading,
      t,
      dashboardToken,
      embedUrl,
      getGroupExploToken,
      getCustomeToken,
      sellerId,
      generateVariablesObject,
    };
  },
  methods: {
    close() {
      ModalModule.setStatus(ModalStatus.Hidden);
    },
    async loadExploScript() {
      // Load the Explo script if it's not already loaded
      return new Promise<void>((resolve, reject) => {
        if (document.getElementById("explo-script")) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.id = "explo-script";
        script.src = "https://embed.explo.co/bundle.js";
        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error("Failed to load the Explo script"));
        document.head.appendChild(script);
      });
    },
  },
  async mounted() {
    try {
      const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;
      // Groups keep Explo until the Data team assigns them the Omni dashboard, and
      // POS-only roles are refused the list (APPS-750)
      const dashboards = await fetchPosDashboards(groupId).catch((error) => {
        LogService.info("Could not list the POS dashboards, using Explo", {
          error: String(error),
        });
        return [] as IPosDashboard[];
      });
      const hasOmniDashboard = dashboards.some(
        (dashboard) =>
          dashboard.providerType === "OMNI" &&
          dashboard.omniDashboardId === "cashier-report-pos"
      );
      if (hasOmniDashboard) {
        const embedUrl = await fetchOmniEmbedUrl({
          groupId,
          dashboardId: "cashier-report-pos",
          locale: getLanguage(),
        });
        if (!embedUrl) {
          throw new Error("No Omni embed URL returned");
        }
        this.embedUrl = embedUrl;
      } else {
        await this.loadExploScript();
        this.dashboardToken = await this.getGroupExploToken(groupId);
      }
      this.isLoading = false;
    } catch (error) {
      LogService.info(
        "Could not load the Cashier report POS dashboard",
        { error: String(error) },
        LogLevel.Error
      );
      NotificationService.showCustomError(this.t("error.load-dashboard"));
      this.close();
    }
  },
});
</script>

<style lang="scss">
$header-height: 45px;
.cashier-report {
  .customized-header {
    padding-left: 30px;
    padding-right: 30px;
    background-color: #004c53;
    height: $header-height;
    color: white;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;

    .header-title p {
      font-size: 18px;
      font-weight: 700;
    }

    .go-back-icon {
      margin-right: 15px;
    }
  }

  .omni-dashboard {
    display: block;
    width: 100%;
    height: calc(100vh - #{$header-height});
    margin-top: $header-height;
    border: none;
  }

  .explo-analytics-dashboard {
    margin-top: $header-height;
  }

  .progressbar-wrapper {
    height: 125px;
    width: 40%;
    margin-left: auto;
    margin-right: auto;
    margin-top: 200px;
    margin-bottom: 40px;
    text-align: center;
    color: #004c53;

    .img-wrapper {
      img {
        width: 200px;
        height: 137px;
      }
    }

    .loading-text {
      font-weight: 600;
      font-size: 17px;
      line-height: 19px;
      margin-bottom: 10px;
    }

    .one-sec-text {
      font-weight: 400;
      font-size: 14px;
      line-height: 16px;
      margin-top: 15px;
    }
  }
}
</style>
